// Updated PrivacyPolicy.jsx with extended policy sections and compliance content

import React, { useState } from "react";
import { Header } from "../components";

const Footer = () => (
  <footer className="bg-[#29313f] text-white p-4 text-center">
    <p>&copy; 2025 Arqelio LLC. All Rights Reserved.</p>
  </footer>
);

const ChevronDownIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AccordionItem = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left text-lg font-semibold text-gray-800 focus:outline-none"
      >
        <span>{title}</span>
        <ChevronDownIcon
          className={`transform transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-screen mt-4" : "max-h-0"
        }`}
      >
        <div className="text-gray-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

const PrivacyPortal = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [consent, setConsent] = useState({ terms: false, marketing: false });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setConsent((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isVerified && consent.terms) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } else {
      const errorEl = document.getElementById("form-error");
      if (errorEl) {
        errorEl.textContent =
          "Please verify you are not a robot and accept the terms of service.";
        setTimeout(() => {
          errorEl.textContent = "";
        }, 3000);
      }
    }
  };

  return (
    <>
      <Header />
      <br />
      <br />
      <br />
      <main className="bg-gray-50">
        <section className="bg-gradient-to-r from-gray-700 to-[#29313f] text-white py-20 pb-4 text-center">
          <div className="container mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Portal</h1>
            <p className="mt-4 text-lg">
              Manage your consent and learn about how we protect your data.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-semibold text-center text-gray-900 mb-10">
              Our Privacy Policies
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <AccordionItem title="1. Information We Collect">
                <p className="mb-4">
                  To provide our services and process job applications
                  effectively, we collect certain types of information. This
                  data is essential for assessing qualifications, maintaining
                  communication, and ensuring a secure experience.
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>
                    <strong>Personal Data:</strong> This includes identifiable
                    information such as your full name, physical address,
                    telephone number, email address, previous employment
                    history, and government-issued identification documents.
                    This information is necessary to verify your identity and
                    evaluate your application.
                  </li>
                  <li>
                    <strong>Device and Usage Data:</strong> We automatically
                    collect information about how you access and use our
                    services. This includes your IP address, browser type,
                    operating system, and geographic location (if you grant
                    permission). This data helps us improve our website's
                    functionality and security.
                  </li>
                  <li>
                    <strong>Cookies and Tracking Technologies:</strong> We use
                    cookies to store your preferences, track user activity, and
                    enhance your overall experience on our site. For a complete
                    overview, please refer to our detailed Cookie Policy section
                    below.
                  </li>
                </ul>
              </AccordionItem>
              <AccordionItem title="2. How We Use Your Information">
                <p>
                  Your data is used strictly for legitimate business purposes.
                  Our primary uses include processing your job application,
                  which involves reviewing your qualifications and contacting
                  you. We also use your data to improve our services by
                  analyzing user behavior to enhance our website's performance
                  and usability. Furthermore, we may send you important updates
                  regarding your application or our services and use your
                  information to comply with legal and regulatory obligations.
                </p>
              </AccordionItem>
              <AccordionItem title="3. Third-Party Data Sharing">
                <p className="mb-4">
                  We do not sell your personal data. However, we may share it
                  with trusted third-party partners to perform essential
                  services on our behalf. We ensure these partners adhere to
                  strict data protection standards. Sharing may occur with:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>
                    <strong>Background Check Providers:</strong> To verify the
                    information provided in your application, such as employment
                    history and criminal records, where legally permissible.
                  </li>
                  <li>
                    <strong>Cloud Hosting Platforms:</strong> To securely store
                    and manage your data on robust, encrypted servers.
                  </li>
                  <li>
                    <strong>Legal and Government Authorities:</strong> We may
                    disclose your information if required by law, a court order,
                    or a government request to protect our legal rights or the
                    safety of others.
                  </li>
                </ul>
              </AccordionItem>
              <AccordionItem title="4. Cookies & Tracking Technologies">
                <p className="mb-4">
                  Cookies are small text files stored on your device that help
                  our website function correctly and provide a personalized
                  experience. We use different types of cookies:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>
                    <strong>Essential Cookies:</strong> These are necessary for
                    the site to work, such as remembering your session.
                  </li>
                  <li>
                    <strong>Performance Cookies:</strong> These help us
                    understand how users interact with our site, allowing us to
                    identify areas for improvement.
                  </li>
                  <li>
                    <strong>Functional Cookies:</strong> These remember your
                    preferences (e.g., language) to enhance your user
                    experience.
                  </li>
                </ul>
                <p className="mt-4">
                  You have the right to control cookie usage. Most web browsers
                  allow you to manage or disable cookies through their settings.
                  Please note that disabling essential cookies may impact the
                  site's functionality.
                </p>
              </AccordionItem>
              <AccordionItem title="5. Children's Privacy">
                <p>
                  Our services are intended for a general audience and are not
                  directed at individuals under the age of 13. We do not
                  knowingly collect, store, or solicit personal information from
                  children. If we become aware that we have inadvertently
                  collected data from a child under 13, we will take immediate
                  steps to delete it. If you are a parent or guardian and
                  believe your child has provided us with personal information,
                  please contact us at our support email.
                </p>
              </AccordionItem>
              <AccordionItem title="6. Data Security Measures">
                <p>
                  We are committed to protecting your data from unauthorized
                  access, alteration, or disclosure. We implement a variety of
                  robust security measures, including data encryption for
                  information both in transit and at rest, strict access control
                  policies to ensure only authorized personnel can view your
                  data, and regular security audits to identify and address
                  potential vulnerabilities. While we take these precautions
                  seriously, no method of electronic storage or transmission is
                  100% secure, and we cannot guarantee absolute security.
                </p>
              </AccordionItem>
              <AccordionItem title="7. Your Rights (GDPR & CCPA Compliance)">
                <p className="mb-4">
                  We respect your data rights. Depending on your geographical
                  location, you are entitled to certain rights under regulations
                  like the GDPR and CCPA. These rights include:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>
                    <strong>Right to Access:</strong> You can request a copy of
                    the personal data we hold about you.
                  </li>
                  <li>
                    <strong>Right to Rectification:</strong> You can ask us to
                    correct any inaccurate or incomplete data.
                  </li>
                  <li>
                    <strong>Right to Erasure:</strong> You can request the
                    deletion of your personal data, subject to certain legal
                    obligations.
                  </li>
                  <li>
                    <strong>Right to Opt-Out:</strong> For California residents,
                    you have the right to opt out of the "sale" of your personal
                    information.
                  </li>
                  <li>
                    <strong>Right to Data Portability:</strong> You can request
                    that we transfer your data to another organization.
                  </li>
                  <li>
                    <strong>Right to Withdraw Consent:</strong> You can withdraw
                    your previously given consent for data processing at any
                    time.
                  </li>
                </ul>
                <p className="mt-4">
                  To exercise any of these rights, please send a detailed
                  request to our privacy team at{" "}
                  <strong>privacy@arqelio.com</strong>.
                </p>
              </AccordionItem>
              <AccordionItem title="8. Updates to This Policy">
                <p>
                  The business and legal landscape is constantly evolving, so we
                  may need to update this privacy policy from time to time. When
                  we do, we will post the updated version on this page and
                  revise the "Last Updated" date. For significant changes, we
                  will make reasonable efforts to notify you, such as by sending
                  an email or displaying a prominent notice on our website. We
                  encourage you to review this page periodically to stay
                  informed.
                </p>
              </AccordionItem>
            </div>
          </div>
        </section>

        <section className="pb-16">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-semibold text-center text-gray-900 mb-10">
              Verification & Consent
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 mb-8">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="terms"
                      checked={consent.terms}
                      onChange={handleConsentChange}
                      className="form-checkbox h-5 w-5 text-indigo-600 rounded"
                    />
                    <span className="text-gray-700">
                      I have read, understood, and agree to the Terms of Service
                      and Privacy Policy.
                    </span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="marketing"
                      checked={consent.marketing}
                      onChange={handleConsentChange}
                      className="form-checkbox h-5 w-5 text-indigo-600 rounded"
                    />
                    <span className="text-gray-700">
                      I consent to receive email updates, newsletters, and
                      promotional materials.
                    </span>
                  </label>
                </div>
                <div className="flex items-center space-x-4 p-4 border border-gray-300 rounded-lg bg-gray-100 mb-8">
                  <div
                    onClick={() => setIsVerified(!isVerified)}
                    className="w-8 h-8 border-2 border-gray-400 rounded-md bg-white flex items-center justify-center cursor-pointer"
                  >
                    {isVerified && <CheckIcon className="text-green-500" />}
                  </div>
                  <span className="text-gray-800">I'm not a robot</span>
                  <img
                    src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                    alt="reCAPTCHA logo"
                    className="ml-auto"
                  />
                </div>
                <div className="text-center">
                  <button
                    type="submit"
                    className={`px-8 py-3 font-semibold rounded-lg text-white transition-all duration-300 ${
                      isVerified && consent.terms
                        ? "bg-indigo-600 hover:bg-indigo-700"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    disabled={!isVerified || !consent.terms}
                  >
                    Save Preferences
                  </button>
                </div>
              </form>
              {showSuccess && (
                <div className="mt-6 p-4 text-center text-green-800 bg-green-100 border border-green-300 rounded-lg">
                  Your preferences have been saved successfully!
                </div>
              )}
              <div
                id="form-error"
                className="mt-4 text-center text-red-600 font-semibold"
              ></div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPortal;
